import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { ClinicFormProps } from '../../types';
import { StyledPaperContainer } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { getClinics, getClinicById } from '../../../../../apis/clinics/requests';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';

interface Clinic {
  _id: string;
  name: string;
}

const CloneClinicSelector = ({ form, selectedClinicToClone, onClinicToCloneChange }: ClinicFormProps) => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClinics = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getClinics();
        if (response && response.data) {
          const formattedClinics = response.data.map((clinic: any) => ({
            _id: clinic._id,
            name: clinic.name
          }));
          setClinics(formattedClinics);
        }
      } catch (err) {
        setError('Failed to load clinics');
        console.error('Error fetching clinics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinics();
  }, []);

  useEffect(() => {
    const loadClinicDetails = async () => {
      if (selectedClinicToClone && selectedClinicToClone !== 'none') {
        setLoading(true);
        try {
          const clinicData = await getClinicById(selectedClinicToClone);
          
          const clinicDataWithId = clinicData as any;
          const { _id, ...clinicWithoutId } = clinicDataWithId;
          
          const nameWithClone = `${clinicData.name} (Clone)`;
          
          form.reset({
            ...clinicWithoutId,
            name: nameWithClone
          });
        } catch (err) {
          setError('Failed to load clinic details');
          console.error('Error loading clinic details:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    loadClinicDetails();
  }, [selectedClinicToClone, form]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const clinicId = event.target.value as string;
    onClinicToCloneChange?.(clinicId === 'none' ? null : clinicId);
    
    if (clinicId === 'none') {
      form.reset(form.formState.defaultValues);
    }
  };

  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <Typography variant="h6">Clone from Existing Clinic</Typography>
        <Typography variant="body2">
          Select an existing clinic to clone all its settings, conditions, and products. You can customise the cloned clinic after creation.
        </Typography>

        <FormControl fullWidth disabled={loading}>
          <InputLabel id="clinic-clone-select-label">Clinic to Clone</InputLabel>
          <Select
            id="clinic-clone-select"
            value={selectedClinicToClone || 'none'}
            onChange={handleChange}
          >
            <MenuItem value="none">None (Create from scratch)</MenuItem>
            {clinics.map((clinic) => (
              <MenuItem key={clinic._id} value={clinic._id}>
                {clinic.name}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText error>{error}</FormHelperText>}
          {loading && <FormHelperText>Loading clinic data...</FormHelperText>}
        </FormControl>
      </ColumnWithGap>
    </StyledPaperContainer>
  );
};

export default CloneClinicSelector;
